/**
 * Error reporting for the three apps — off unless the server says otherwise.
 *
 * The apps are built once and served anywhere, so nothing here is baked in at
 * build time. Each page loads `/config.js` before the app; in production Caddy
 * fills it from its own environment (`deploy/Caddyfile`), in development it is
 * the empty file in each app's `public/`. With no DSN in it, this does nothing
 * at all: the Sentry SDK is a separate chunk that is never even downloaded.
 *
 * With one, uncaught errors and the ones a `Boundary` catches are sent — the
 * second half matters most, because a panel that breaks and shows its error is
 * exactly the failure nobody would otherwise hear about. Nothing personal goes
 * with them: no cookies, no request headers, no query strings (a signed file
 * URL is a credential for as long as it lasts), and Sentry's own collection of
 * personal data stays off — explicitly, category by category, because the
 * SDK's defaults collect most of it.
 */

interface AppConfig {
  sentryDsn?: string;
  environment?: string;
  release?: string;
}

declare global {
  interface Window {
    __APP_CONFIG__?: AppConfig;
  }
}

type Capture = (error: unknown, context: Record<string, string>) => void;

let capture: Capture | null = null;

function withoutQuery(url: string): string {
  const cut = url.search(/[?#]/);
  return cut === -1 ? url : url.slice(0, cut);
}

/** Start reporting if this deployment has a DSN. Call once, before rendering. */
export function startErrorReporting(app: 'pos' | 'kds' | 'admin' | 'platform'): void {
  const config = typeof window === 'undefined' ? undefined : window.__APP_CONFIG__;
  const dsn = config?.sentryDsn;
  if (!dsn) return;
  void import('@sentry/react')
    .then((Sentry) => {
      Sentry.init({
        dsn,
        ...(config.environment ? { environment: config.environment } : {}),
        ...(config.release ? { release: config.release } : {}),
        // Every category of collected data, off. The SDK's defaults take user
        // details, cookies, headers, query strings and request *and response*
        // bodies — which on a till means guests' names and what they ordered.
        dataCollection: {
          userInfo: false,
          cookies: false,
          httpHeaders: false,
          httpBodies: [],
          urlQueryParams: false,
        },
        tracesSampleRate: 0,
        initialScope: { tags: { app } },
        beforeBreadcrumb(crumb) {
          const url: unknown = crumb.data?.['url'];
          if (typeof url === 'string' && crumb.data) crumb.data['url'] = withoutQuery(url);
          return crumb;
        },
        beforeSend(event) {
          if (event.request) {
            delete event.request.cookies;
            delete event.request.headers;
            if (event.request.url) event.request.url = withoutQuery(event.request.url);
            delete event.request.query_string;
          }
          return event;
        },
      });
      capture = (error, context) => Sentry.captureException(error, { tags: context });
    })
    // Reporting must never be the thing that breaks the till.
    .catch(() => undefined);
}

/**
 * Report an error something caught — a `Boundary`, mostly. A no-op until (and
 * unless) reporting has started.
 */
export function reportError(error: unknown, context: Record<string, string> = {}): void {
  capture?.(error, context);
}
